import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MdInfoOutline } from "react-icons/md";
import { Banner, Disclosure, Progress } from "../../components/mui";

/**
 * 專案詳情的摘要卡（設計稿 #screen-project，el-pm-template_4.html L766-L789）
 *
 * 這張卡取代舊版的兩樣東西：
 *   · 頁首那排「數字牆」（進度 73%／22 件／3 人 各佔一格）——
 *     數字排在一起看得到卻讀不出意思，使用者還要自己在心裡把它們兜成一句話。
 *     這裡改成整句敘述：「有 22 件工項，做完 16 件、還剩 6 件；今天有 1 件要交。」
 *   · 「專案資訊」彈窗 —— 內容全部搬進最底下的收合區，不再是一個要先點開才看得到、
 *     點開之後又蓋住整頁的視窗（硬規則 8：功能一項都不能少，只是換地方擺）。
 *
 * 句子裡的每一個數字都由 Detail.js 依真實資料算出來（硬規則 10），
 * 算不出來的（沒有工項、沒有人負責）就整句不寫，不要拿別的欄位頂替。
 *
 * 它現在住在頁首的「⋯ 更多」裡（Detail.js 的 #pd-more），五顆分頁都展開得到：
 * 以前它只擋在工項分頁的清單前面，一切分頁頁首就變高變矮，工項分頁看起來像另一頁。
 * 原本「更多」彈窗裡的編輯專案、刪除專案也一併排進這張卡最底下的動作列，彈窗拿掉了。
 */

/* 這張卡不自己取資料，全部由 Detail.js 算好傳進來 —— 同一份數字別在兩個地方各算一次 */

function DetailSummary({
  headline,
  workload,
  pct,
  showProgress,
  progressTone,
  contradiction,
  info,
  canCreateTask,
  canEditProject,
  canDeleteProject,
  onAddTask,
  onImport,
  onEditProject,
  onDeleteProject,
}) {
  const hasActions = canCreateTask || canEditProject || canDeleteProject;
  return (
    <Card component="section" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h3" component="h2">
          專案概況
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {/*
            進度條。max-width 520px 是設計稿 L769 寫死在這一條上的值 ——
            這是寬度不是顏色或字級，大字模式仍然照常放大。
            還沒有工項的專案算不出進度，整條不畫（硬規則 2）。
          */}
          {showProgress && (
            <Progress value={pct} tone={progressTone} ariaLabel={`專案進度 ${pct}%`} sx={{ maxWidth: 520 }} />
          )}

          <Typography>{headline}</Typography>
          {/* 誰扛多少：兩個人以上才講得出「不平均」，只有一個人時這句話沒有意義 */}
          {workload ? <Typography>{workload}</Typography> : null}
        </Stack>

        {/*
          資料矛盾要自己講出來，不要讓使用者以為是自己看錯（設計稿 L777-781）。
          寫成提示條而不是灰字小字（硬規則 4）。
        */}
        {contradiction ? (
          <Banner kind="info" icon={<MdInfoOutline />} sx={{ mt: 2 }}>
            <strong>{contradiction.title}　</strong>
            {contradiction.text}
          </Banner>
        ) : null}

        {/*
          舊的「專案資訊」彈窗內容。每一項都還在，只是收進收合區 ——
          這些值（客戶、優先級、預估工時、Git 連結）多數專案一年也不會改一次，
          擺在第一屏只會把真正要看的工項擠到畫面外。
        */}
        {info.length > 0 && (
          <Disclosure title="專案資訊" count={`（${info.length} 項）`} sx={{ mt: 3 }}>
            <Stack spacing={1}>
              {info.map((row) => (
                <Typography key={row.label}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {row.label}：
                  </Typography>
                  {row.href ? (
                    /* 連結也要 ≥44px 且有文字（硬規則 3）—— Button 的高度吃 --t-control-h */
                    <Button variant="text" href={row.href} target="_blank" rel="noreferrer">
                      {row.value}
                    </Button>
                  ) : (
                    row.value
                  )}
                </Typography>
              ))}
            </Stack>
          </Disclosure>
        )}

        {/*
          動作列（設計稿 L783-788 的那一排，加上原本「⋯ 更多」彈窗裡的編輯與刪除）。
          這張卡已經在「更多」裡面，所以原本那顆「⋯ 更多」拿掉 —— 更多裡面再放一顆更多是繞圈。

          四顆一律不是 primary（硬規則 1）：這一區是使用者自己展開的，會跟分頁內容同時在畫面上，
          而會議紀錄分頁的空狀態已經有一顆 primary，這裡再放一顆就是同一個畫面兩顆主鈕。
          新增工項平常走工項分頁清單底下的「＋ 新增工項」，這裡是順手的另一個入口，不必搶眼。
          刪除用 error 色，按下去只是打開 Detail.js 的確認視窗，不會一按就刪。
          「編輯專案」不再寫成「編輯專案（名稱、說明、代碼、程式碼位置）」：放在一排裡的
          按鈕不換行，那麼長的字在手機寬度會把按鈕撐出畫面。

          一顆都沒有權限的人整排不畫（硬規則 2），只留下面那句話。
        */}
        {hasActions && (
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 3 }}>
            {canCreateTask && (
              <Button variant="outlined" type="button" data-tour="add-item" onClick={onAddTask}>
                新增工項
              </Button>
            )}
            {canCreateTask && (
              <Button variant="outlined" type="button" onClick={onImport}>
                從 Excel 匯入工項
              </Button>
            )}
            {canEditProject && (
              <Button variant="outlined" type="button" onClick={onEditProject}>
                編輯專案
              </Button>
            )}
            {canDeleteProject && (
              <Button variant="outlined" color="error" type="button" onClick={onDeleteProject}>
                刪除專案
              </Button>
            )}
          </Stack>
        )}
        {/* 原本彈窗裡的那句話原封不動搬過來：沒有按鈕的地方要講為什麼沒有，不然像是東西掉了 */}
        {!canEditProject && !canDeleteProject && (
          <Typography sx={{ mt: 2 }}>您的權限僅能檢視此專案，無法編輯或刪除。</Typography>
        )}
      </CardContent>
    </Card>
  );
}

DetailSummary.propTypes = {
  /** 整句敘述：「這個專案有 22 件工項，做完 16 件…」 */
  /** 一句話的概況；工項還在讀時是 <InlineLoading />，所以是 node */
  headline: PropTypes.node.isRequired,
  /** 「誰扛多少」那一句；算不出來（沒有人被指派）時傳空字串，整句不畫 */
  workload: PropTypes.string,
  pct: PropTypes.number,
  showProgress: PropTypes.bool,
  /** 舊的 el-progress 類別字串，只用來判斷顏色（done／attention…） */
  progressTone: PropTypes.oneOf(["progress", "done", "attention", "warn", "closed"]),
  /** { title, text }；沒有矛盾時傳 null */
  contradiction: PropTypes.object,
  /** 舊「專案資訊」彈窗的內容，[{ label, value, href? }]；沒有值的欄位不要放進來 */
  info: PropTypes.array,
  canCreateTask: PropTypes.bool,
  /** project.edit：有才放「編輯專案」 */
  canEditProject: PropTypes.bool,
  /** project.delete：有才放「刪除這個專案」 */
  canDeleteProject: PropTypes.bool,
  onAddTask: PropTypes.func,
  onImport: PropTypes.func,
  onEditProject: PropTypes.func,
  /** 只負責打開刪除確認視窗；真的刪除在 Detail.js 的 deleteProject */
  onDeleteProject: PropTypes.func,
};

DetailSummary.defaultProps = {
  workload: "",
  pct: 0,
  showProgress: false,
  progressTone: "progress",
  contradiction: null,
  info: [],
  canCreateTask: false,
  canEditProject: false,
  canDeleteProject: false,
  onAddTask: () => {},
  onImport: () => {},
  onEditProject: () => {},
  onDeleteProject: () => {},
};

export default DetailSummary;
